import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import RegistrasiService from "../../../services/registrasi/RegistrasiService.js";
import {
    initialTrader,
    initialUser,
    generateCaptcha,
} from "../utils/registerUtils.js";

export const useRegister = () => {
    const navigate = useNavigate();
    const RegService = useMemo(() => new RegistrasiService(), []);

    const [trader, setTrader] = useState(initialTrader);
    const [user, setUser] = useState(initialUser);

    const [kodeUPT, setKodeUPT] = useState("");
    const [namaUPT, setNamaUPT] = useState("");
    const [trade, setTrade] = useState(null);
    const [tradeFound, setTradeFound] = useState(false);

    const [propinsi, setPropinsi] = useState([]);
    const [kota, setKota] = useState([]);

    const [services, setServices] = useState({ first: false, second: false });
    const [files, setFiles] = useState({ npwp: "", ktp: "", nib: "" });

    const [captcha, setCaptcha] = useState(generateCaptcha);
    const [textCaptcha, setTextCaptcha] = useState("");

    const [showUPT, setShowUPT] = useState(false);
    const [agreementChecked, setAgreementChecked] = useState(false); // ← BARU
    const [isSubmitting, setIsSubmitting] = useState(false);          // ← BARU

    const [alerts, setAlerts] = useState({
        uploadNPWP: false,
        uploadKTP: false,
        uploadNIB: false,
        fileTooLarge: false,
        pilihService: false,
        pilihUPT: false,
        validCaptcha: false,
        existingUser: false,
        belumSetuju: false,   // ← BARU: guard kalau agreement belum dicentang
        selesai: false,
        tradeFound: false,
        serverError: false,
    });

    const setAlert = (key, value) =>
        setAlerts((prev) => ({ ...prev, [key]: value }));

    // ── Init ──────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            const [propRes, folderRes] = await Promise.all([
                RegService.getPropinsi(),
                RegService.getFolderName(),
            ]);
            setPropinsi(propRes.data);
            setTrader((prev) => ({ ...prev, FILE_ID: folderRes.data }));
            setCaptcha(generateCaptcha());
        };
        init();
    }, []);

    useEffect(() => {
        setUser((prev) => ({ ...prev, KD_UNIT: kodeUPT }));
    }, [kodeUPT]);

    useEffect(() => {
        if (trade) {
            setUser((prev) => ({ ...prev, KODE_TRADER: trade.KODE_TRADER }));
            loadKota(trade.PROPINSI);
        }
    }, [tradeFound]);

    // ── Helpers ───────────────────────────────────
    const loadKota = async (kode) => {
        if (!kode) return;
        const res = await RegService.getKota(kode);
        setKota(res.data);
    };

    const handlePropinsiChange = async (e) => {
        setTrader((prev) => ({ ...prev, PROPINSI: e.target.value }));
        await loadKota(e.target.value);
    };

    const refreshCaptcha = () => setCaptcha(generateCaptcha());

    // ── NPWP lookup ───────────────────────────────
    const handleNPWPBlur = async (e) => {
        try {
            const res = await RegService.getTraderNPWP(e.target.value);
            if (res.data) {
                setTrade(res.data);
                setTradeFound(true);
                const userRes = await RegService.getUser(res.data.KODE_TRADER);
                setUser(userRes.data);
                setAlert("tradeFound", true);
            } else {
                setTrade(null);
                setTradeFound(false);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // ── Upload file ke file-server ─────────────────
    const uploadFiles = async (fileId) => {
        const dokumen = [];

        const uploadList = [
            { file: files.npwp, dokKode: 1 },
            { file: files.ktp, dokKode: 2 },
            { file: files.nib, dokKode: 3 },
        ];

        for (const { file, dokKode } of uploadList) {
            await RegService.putDokumen(fileId, file);
            dokumen.push({
                DOK_KODE: String(dokKode),
                DOK_PATH: `${process.env.REACT_APP_API_URL_FILE}/view/${fileId}/${file.name}`,
            });
        }

        return dokumen;
    };

    // ── Hitung services array ─────────────────────
    const buildServicesArray = () => {
        if (services.first && services.second) return [3];
        const result = [];
        if (services.first) result.push(1);
        if (services.second) result.push(2);
        return result;
    };

    // ── Submit ────────────────────────────────────
    const saveTrader = async (e) => {
        e.preventDefault();

        // Guard: user agreement wajib dicentang (selain disable tombol,
        // ini jaga-jaga kalau form ke-submit lewat cara lain, mis. tekan Enter)
        if (!agreementChecked) return setAlert("belumSetuju", true);

        // Validasi frontend tetap ada sebagai UX guard
        if (!files.npwp) return setAlert("uploadNPWP", true);
        if (!files.ktp) return setAlert("uploadKTP", true);
        if (!files.nib) return setAlert("uploadNIB", true);

        // ── Validasi ukuran file (max 1MB per file) ──
        const MAX_SIZE = 1 * 1024 * 1024;
        if (files.npwp.size > MAX_SIZE) return setAlert("fileTooLarge", true);
        if (files.ktp.size > MAX_SIZE) return setAlert("fileTooLarge", true);
        if (files.nib.size > MAX_SIZE) return setAlert("fileTooLarge", true);

        if (user.KD_UNIT === "1" || !kodeUPT) return setAlert("pilihUPT", true);
        if (textCaptcha !== captcha) return setAlert("validCaptcha", true);
        if (!services.first && !services.second) return setAlert("pilihService", true);

        setIsSubmitting(true);
        try {
            // 1. Upload semua file terlebih dahulu
            const dokumen = await uploadFiles(trader.FILE_ID);

            // 2. Kirim satu payload ke endpoint registerFull
            await RegService.registerFull({
                trader,
                user,
                kodeUPT,
                services: buildServicesArray(),
                fileId: trader.FILE_ID,
                dokumen,
            });

            setAlert("selesai", true);
        } catch (err) {
            console.error(err);
            // Cek apakah error dari BE (username duplikat, dll)
            const msg = err?.response?.data?.msg;
            if (msg === "Username sudah digunakan") {
                setAlert("existingUser", true);
            } else {
                setAlert("serverError", true);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        trader, setTrader,
        user, setUser,
        trade, tradeFound,
        propinsi, kota,
        kodeUPT, setKodeUPT,
        namaUPT, setNamaUPT,
        services, setServices,
        files, setFiles,
        captcha, textCaptcha, setTextCaptcha,
        showUPT, setShowUPT,
        agreementChecked, setAgreementChecked, // ← BARU
        isSubmitting,                          // ← BARU
        alerts, setAlert,
        saveTrader,
        handleNPWPBlur,
        handlePropinsiChange,
        refreshCaptcha,
        navigate,
    };
};